import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Space,
  Modal,
  message,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Typography,
  Tooltip,
  Avatar,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Patient } from '../types/patient';
import { patientService } from '../services/patientService';
import PatientForm from '../components/patients/PatientForm';
import PatientDetails from '../components/patients/PatientDetails';

const { Title } = Typography;
const { Search } = Input;

const Patients: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    filterPatients();
  }, [patients, searchText]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const data = await patientService.getPatients();
      setPatients(data);
    } catch (error) {
      message.error('Failed to fetch patients');
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPatients = () => {
    if (!searchText) {
      setFilteredPatients(patients);
      return;
    }

    const filtered = patients.filter(
      (patient) =>
        patient.name.toLowerCase().includes(searchText.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        patient.phone?.includes(searchText) ||
        patient.medical_record_number?.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredPatients(filtered);
  };

  const handleAddPatient = () => {
    setEditingPatient(null);
    setIsModalVisible(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setEditingPatient(patient);
    setIsModalVisible(true);
  };

  const handleViewPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsDetailsModalVisible(true);
  };

  const handleDeletePatient = async (id: number) => {
    try {
      await patientService.deletePatient(id);
      message.success('Patient deleted successfully');
      fetchPatients();
    } catch (error) {
      message.error('Failed to delete patient');
      console.error('Error deleting patient:', error);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingPatient(null);
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalVisible(false);
    setSelectedPatient(null);
  };

  const handlePatientSaved = () => {
    fetchPatients();
    handleModalClose();
  };

  const getPatientAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getInitials = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const columns = [
    {
      title: 'Patient',
      key: 'patient',
      render: (record: Patient) => (
        <div className="flex items-center space-x-3">
          <Avatar 
            size="default" 
            className="bg-blue-500 text-white"
            style={{ 
              backgroundColor: '#3b82f6', 
              color: 'white',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {getInitials(record.name)}
          </Avatar>
          <div>
            <div className="font-medium text-gray-900">
              {record.name}
            </div>
            <div className="text-sm text-gray-500">
              Patient ID: {record.patient_id || record.id}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record: Patient) => (
        <div>
          <div className="text-sm text-gray-900">{record.email || 'No email'}</div>
          <div className="text-sm text-gray-500">{record.phone || 'No phone'}</div>
        </div>
      ),
    },
    {
      title: 'Medical Record #',
      dataIndex: 'medical_record_number',
      key: 'medical_record_number',
      render: (text: string) => text || 'Not assigned',
    },
    {
      title: 'Insurance',
      dataIndex: 'insurance_info',
      key: 'insurance_info',
      render: (text: string) => text || 'Not specified',
      ellipsis: true,
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Patient) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewPatient(record)}
              className="text-blue-600 hover:text-blue-800"
            />
          </Tooltip>
          <Tooltip title="Edit Patient">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditPatient(record)}
              className="text-green-600 hover:text-green-800"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this patient?"
            description="This action cannot be undone and will remove all associated visits."
            onConfirm={() => handleDeletePatient(record.id)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Tooltip title="Delete Patient">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className="text-red-600 hover:text-red-800"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Title level={2} className="!mb-2">
            <UserOutlined className="mr-2" />
            Patient Management
          </Title>
          <p className="text-gray-600">
            Manage your patient profiles and track their visits
          </p>
        </div>

        {/* Stats Cards */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card className="text-center">
              <div className="text-2xl font-bold text-blue-600">{patients.length}</div>
              <div className="text-gray-600">Total Patients</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {patients.filter(p => new Date(p.updated_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length}
              </div>
              <div className="text-gray-600">Active This Month</div>
            </Card>
          </Col>
        </Row>

        {/* Controls */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <Search
              placeholder="Search patients by name, email, phone, or MRN..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full sm:w-96"
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={handleAddPatient}
              className="w-full sm:w-auto"
            >
              Add New Patient
            </Button>
          </div>
        </Card>

        {/* Patients Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredPatients}
            rowKey="id"
            loading={loading}
            pagination={{
              total: filteredPatients.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} patients`,
            }}
            className="overflow-x-auto"
          />
        </Card>

        {/* Add/Edit Patient Modal */}
        <Modal
          title={editingPatient ? 'Edit Patient' : 'Add New Patient'}
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={null}
          width={800}
          destroyOnClose
        >
          <PatientForm
            patient={editingPatient}
            onSave={handlePatientSaved}
            onCancel={handleModalClose}
          />
        </Modal>

        {/* Patient Details Modal */}
        <Modal
          title="Patient Details"
          open={isDetailsModalVisible}
          onCancel={handleDetailsModalClose}
          footer={null}
          width={1000}
          destroyOnClose
        >
          {selectedPatient && (
            <PatientDetails
              patient={selectedPatient}
              onEdit={() => {
                handleDetailsModalClose();
                handleEditPatient(selectedPatient);
              }}
              onClose={handleDetailsModalClose}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Patients; 